import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import { mnemonicToAccount } from "viem/accounts";
import { mnemonicToSeedSync, validateMnemonic } from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/english";
import { VersionedTransaction } from "@solana/web3.js";
import { Account, AccountType, getVirtualEnvironment, notEmpty, validateAndFormatAddress, validateAndLowerCase, VMType, } from "@orb-labs/types";
import { Buffer } from "buffer";
import { Solana } from "../../bullrun/solana.js";
import { usePlatformStorage } from "../../storage/PlatformStorageContext.js";
const MnemonicContext = createContext(undefined);
export const useMnemonic = () => {
    const context = useContext(MnemonicContext);
    if (!notEmpty(context)) {
        throw new Error("useMnemonic must be used within a MnemonicProvider");
    }
    return context;
};
export const MnemonicProvider = ({ checkSecurity, children }) => {
    const storage = usePlatformStorage();
    const [accounts, setAccounts] = useState([]);
    const [hasMnemonic, setHasMnemonic] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(undefined);
    const loadStartedRef = useRef(false);
    const deriveAccountsFromMnemonic = useCallback(async (mnemonic) => {
        try {
            const derivedAccounts = [];
            try {
                const evmAccount = mnemonicToAccount(mnemonic, { accountIndex: 0 });
                derivedAccounts.push(new Account(evmAccount.address, AccountType.EOA, VMType.EVM, undefined));
            }
            catch (error) {
                console.error("[MnemonicProvider] Error deriving EVM wallet:", error);
            }
            try {
                const seed = mnemonicToSeedSync(mnemonic);
                const seedArrayBuffer = new Uint8Array(seed).buffer;
                const keypair = await Solana.getKeypair({
                    seed: { data: seedArrayBuffer },
                    extendedPublicKey: new ArrayBuffer(0),
                    chainCode: null,
                    accountIdx: 0,
                });
                derivedAccounts.push(new Account(keypair.publicKey.toString(), AccountType.EOA, VMType.SVM, undefined));
            }
            catch (error) {
                console.error("[MnemonicProvider] Error deriving SVM wallet:", error);
            }
            return derivedAccounts;
        }
        catch (error) {
            console.error("[MnemonicProvider] Error deriving wallets from mnemonic:", error);
            return [];
        }
    }, []);
    /**
     * Load mnemonic accounts once per mount. Guard with a ref so we never run
     * getMnemonic() twice (avoids duplicate passcode prompts from React Strict Mode
     * or effect re-runs).
     */
    useEffect(() => {
        if (loadStartedRef.current)
            return;
        loadStartedRef.current = true;
        const loadMnemonicWallets = async () => {
            setIsLoading(true);
            setLoadError(undefined);
            try {
                const mnemonic = await storage.getMnemonic();
                if (mnemonic) {
                    const derivedAccounts = await deriveAccountsFromMnemonic(mnemonic);
                    setAccounts(derivedAccounts);
                    setHasMnemonic(true);
                }
                else {
                    // Storage responded cleanly with "nothing stored" — genuine new-user
                    // state. Safe for the UI to offer a fresh import flow.
                    setAccounts([]);
                    setHasMnemonic(false);
                }
            }
            catch (error) {
                // Storage threw — keychain corruption, biometric failure, platform
                // error. We do NOT know whether a mnemonic exists. Surface the error
                // so consumers can show a retry/recovery UI instead of silently
                // offering a fresh-import flow that would overwrite the user's wallet.
                console.error("[MnemonicProvider] Error loading mnemonic wallets:", error);
                setAccounts([]);
                setHasMnemonic(false);
                setLoadError(error instanceof Error ? error : new Error(String(error)));
            }
            finally {
                setIsLoading(false);
            }
        };
        loadMnemonicWallets();
    }, [deriveAccountsFromMnemonic, storage]);
    const importWallet = useCallback(async (mnemonic) => {
        checkSecurity();
        try {
            // BIP-39 English wordlist is lowercase; normalize user input so casing
            // differences don't fail validation. This is plain language text, not
            // an address or VM-typed value — validateAndLowerCase handles the case
            // fold while satisfying the lint rule against raw .toLowerCase().
            const normalizedMnemonic = validateAndLowerCase(mnemonic.trim());
            if (!notEmpty(normalizedMnemonic)) {
                throw new Error("Mnemonic phrase cannot be empty");
            }
            const words = normalizedMnemonic.split(/\s+/).filter((word) => word.length > 0);
            if (words.length !== 12 && words.length !== 24) {
                throw new Error("Mnemonic phrase must be exactly 12 or 24 words");
            }
            if (!validateMnemonic(normalizedMnemonic, wordlist)) {
                throw new Error("Invalid mnemonic phrase. Please check your words and try again.");
            }
            const success = await storage.storeMnemonic(normalizedMnemonic);
            if (success) {
                const derivedAccounts = await deriveAccountsFromMnemonic(normalizedMnemonic);
                setAccounts(derivedAccounts);
                setHasMnemonic(true);
            }
            return success;
        }
        catch (error) {
            const message = error instanceof Error ? error.message : "Failed to import wallet";
            throw new Error(message);
        }
    }, [deriveAccountsFromMnemonic, storage, checkSecurity]);
    const clearMnemonicFn = useCallback(async () => {
        await storage.clearMnemonic();
        setAccounts([]);
        setHasMnemonic(false);
    }, [storage]);
    const getEvmLocalAccount = useCallback(async () => {
        checkSecurity();
        const mnemonic = await storage.getMnemonic();
        if (!mnemonic) {
            throw new Error("No mnemonic wallet available for signing");
        }
        return mnemonicToAccount(mnemonic, { accountIndex: 0 });
    }, [storage, checkSecurity]);
    const signEvmTransaction = useCallback(async (operation) => {
        const account = await getEvmLocalAccount();
        const chainId = Number(operation.platformId);
        if (validateAndFormatAddress(account?.address, VMType.EVM) !== validateAndFormatAddress(operation?.from, VMType.EVM)) {
            throw new Error(`Address mismatch: ${validateAndFormatAddress(account.address, VMType.EVM)} != ${validateAndFormatAddress(operation.from ?? "", VMType.EVM)}`);
        }
        // Validate required EVM signing fields up front — Number(undefined) is
        // NaN and would silently produce an invalid signature. Fail loud instead.
        if (!notEmpty(operation.nonce)) {
            throw new Error("Cannot sign EVM transaction: missing nonce");
        }
        if (!notEmpty(operation.gasLimit)) {
            throw new Error("Cannot sign EVM transaction: missing gasLimit");
        }
        const isEip1559 = notEmpty(operation.maxFeePerGas);
        if (isEip1559) {
            if (!notEmpty(operation.maxPriorityFeePerGas)) {
                throw new Error("Cannot sign EIP-1559 transaction: missing maxPriorityFeePerGas");
            }
        }
        else if (!notEmpty(operation.gasPrice)) {
            throw new Error("Cannot sign legacy EVM transaction: missing gasPrice");
        }
        try {
            if (isEip1559) {
                return await account.signTransaction({
                    type: "eip1559",
                    to: operation.to,
                    data: operation.data,
                    value: operation.value ?? BigInt(0),
                    nonce: Number(operation.nonce),
                    gas: operation.gasLimit,
                    maxFeePerGas: operation.maxFeePerGas,
                    maxPriorityFeePerGas: operation.maxPriorityFeePerGas,
                    chainId,
                });
            }
            return await account.signTransaction({
                type: "legacy",
                to: operation.to,
                data: operation.data,
                value: operation.value ?? BigInt(0),
                nonce: Number(operation.nonce),
                gas: operation.gasLimit,
                gasPrice: operation.gasPrice,
                chainId,
            });
        }
        catch (error) {
            console.error("[MnemonicProvider] Error signing EVM transaction:", error);
            throw error;
        }
    }, [getEvmLocalAccount]);
    const signEvmTypedData = useCallback(async (operation) => {
        const account = await getEvmLocalAccount();
        try {
            const typedData = JSON.parse(operation.data);
            return await account.signTypedData({
                domain: typedData.domain,
                types: typedData.types,
                primaryType: typedData.primaryType,
                message: typedData.message,
            });
        }
        catch (error) {
            console.error("[MnemonicProvider] Error signing EVM typed data:", error);
            throw error;
        }
    }, [getEvmLocalAccount]);
    const getSvmKeypair = useCallback(async () => {
        checkSecurity();
        const mnemonic = await storage.getMnemonic();
        if (!mnemonic) {
            throw new Error("No mnemonic wallet available for signing");
        }
        const seed = mnemonicToSeedSync(mnemonic);
        const seedHex = Buffer.from(seed).toString("hex");
        return Solana.getSolanaKeyPair(seedHex, Solana.getDerivationPath(0));
    }, [storage, checkSecurity]);
    const signSvmTransaction = useCallback(async (operation) => {
        const keypair = await getSvmKeypair();
        if (validateAndFormatAddress(keypair.publicKey.toString(), VMType.SVM) !==
            validateAndFormatAddress(operation?.from, VMType.SVM)) {
            throw new Error(`Address mismatch: ${validateAndFormatAddress(keypair.publicKey.toString())} != ${validateAndFormatAddress(operation?.from ?? "")}`);
        }
        try {
            const txHex = operation.data.startsWith("0x") ? operation.data.slice(2) : operation.data;
            const transaction = VersionedTransaction.deserialize(Buffer.from(txHex, "hex"));
            transaction.sign([keypair]);
            return Buffer.from(transaction.serialize()).toString("hex");
        }
        catch (error) {
            console.error("[MnemonicProvider] Error signing SVM transaction:", error);
            throw error;
        }
    }, [getSvmKeypair]);
    const signTransaction = useCallback(async (operation) => {
        const vmType = getVirtualEnvironment(operation.platformId);
        if (vmType === VMType.SVM) {
            return signSvmTransaction(operation);
        }
        else if (vmType === VMType.EVM) {
            return signEvmTransaction(operation);
        }
        throw new Error(`No wallet found for address: ${validateAndFormatAddress(operation.from ?? "")}`);
    }, [signEvmTransaction, signSvmTransaction]);
    const signTypedData = useCallback(async (operation) => signEvmTypedData(operation), [signEvmTypedData]);
    const isOwnAddress = useCallback((address) => {
        const normalized = validateAndFormatAddress(address);
        return accounts.some((account) => validateAndFormatAddress(account.address) === normalized);
    }, [accounts]);
    const contextValue = useMemo(() => ({
        accounts,
        hasMnemonic,
        isLoading,
        loadError,
        importWallet,
        clearMnemonic: clearMnemonicFn,
        reset: clearMnemonicFn,
        signTransaction,
        signTypedData,
        isOwnAddress,
    }), [
        accounts,
        hasMnemonic,
        isLoading,
        loadError,
        importWallet,
        clearMnemonicFn,
        signTransaction,
        signTypedData,
        isOwnAddress,
    ]);
    return _jsx(MnemonicContext.Provider, { value: contextValue, children: children });
};
